/**
 * AtlasMenuBarModule — the SWG-style always-visible toolbar: every feature panel
 * is one CLICK away, so no keyboard shortcut is ever required.
 *
 * Implementation: each button dispatches a SYNTHETIC keydown on document.body
 * (bubbles to the window listeners every panel module already installs), so all
 * panels work unmodified and the toolbar can never drift out of sync with the
 * real bindings — a button IS its hotkey. Buttons carry the hotkey in their
 * tooltip, SWG-style. Dispatching on body (not window) keeps `e.target` a real
 * element so the modules' INPUT/TEXTAREA/closest() guards behave.
 *
 * TWO BARS (2026-08-24): the ground bar (also inside a station / ship deck —
 * atlas.spaceInterior) and a compact FLIGHT bar while the space sector is
 * live (atlas.spaceFlight && !spaceInterior): galactic + system charts, nav,
 * hyperdrive, target, dock / land / station / deck, power presets, missile,
 * countermeasures, droids. The flight bar is narrow on purpose — it sits
 * between the ship-status panel (bottom-left) and the sensor radar
 * (bottom-right); the hud-overlap gate holds both layouts.
 *
 * The bar collapses to a single ≡ chip (persisted); the Debug button appears
 * only under the same dev gate as the debug console itself. In VR the DOM is
 * invisible — AtlasVRMenuModule is the in-headset counterpart.
 */

import { atlas } from '../state.js';
import type { App, IModule } from 'enginex';
import { bus } from '../bus.js';
import { makeIcon } from '../lib/icons.js';
import { keyBindings, type KeyActionName } from '../lib/keybinds.js'; // space campaign I: the bar presses the current binding

interface MenuEntry {
  code: string;
  key?: string;
  /** icons.ts glyph name (stroke SVG — no emoji). */
  icon: string;
  label: string;
  hint: string;
  devOnly?: boolean;
  group: number; // separator grouping
  /** A bus event to emit INSTEAD of a key (new entries use this: no synthesized KeyboardEvent). */
  emit?: 'atlas:open-federal' | 'atlas:open-city-console' | 'atlas:open-droids' | 'atlas:open-town-life' | 'atlas:open-field-guide' | 'atlas:open-invite' | 'atlas:open-bank' | 'atlas:open-voice-rooms';
  /** Space campaign I: press this action's CURRENT first binding (THE ONE-KEY LAW) instead of the literal `code`. */
  action?: KeyActionName;
}

const ENTRIES: MenuEntry[] = [
  { group: 0, code: 'KeyH', key: 'h', icon: 'help', label: 'Help', hint: 'Field manual — everything you can do, searchable (H)' },
  { group: 0, code: 'Escape', key: 'Escape', icon: 'gear', label: 'Options', hint: 'Graphics & options (Esc)' },
  { group: 1, code: 'KeyM', icon: 'map', label: 'Local', hint: 'Local map — surroundings, entities & structures (M)' },
  { group: 1, code: 'KeyN', icon: 'map', label: 'Map', hint: 'World Atlas — settlements & civilizations (N)' },
  { group: 1, code: 'F8', key: 'F8', icon: 'globe', label: 'Worlds', hint: 'My Worlds — create, share, hop (F8)' },
  { group: 1, code: 'F9', key: 'F9', icon: 'globe', label: 'Hive', hint: 'Merchant Hive — teleport there & back (F9)' },
  { group: 1, code: 'Tab', key: 'Tab', icon: 'swap', label: 'Swap', hint: 'Toggle Commons ↔ personal world (Tab)' },
  { group: 2, code: 'KeyP', icon: 'passport', label: 'Passport', hint: 'Passport, avatar, inventory, market (P)' },
  { group: 2, code: 'KeyC', icon: 'compass', label: 'Compass', hint: 'Social Compass — Want/Fear (C)' },
  { group: 2, code: 'KeyO', icon: 'orbit', label: 'Orbits', hint: 'Orbit roster (O)' },
  { group: 2, code: 'KeyG', icon: 'people', label: 'Social', hint: 'Social — party, friends, messages, trade, safety (G)' },
  { group: 2, code: 'KeyV', icon: 'build', label: 'Guilds', hint: 'Guilds — charter, roster, elections (V)' },
  { group: 2, code: '', icon: 'people', label: 'Assembly', hint: 'The federal docket — Speaker, ministers, the bill before the Assembly, works, the Tribunal', emit: 'atlas:open-federal' },
  { group: 2, code: '', icon: 'build', label: 'City', hint: 'City console — found a city, residents, buildings, taxes, defense, civilisation, elections', emit: 'atlas:open-city-console' },
  { group: 2, code: '', icon: 'trophy', label: 'Town', hint: 'Town life — festivals, wars, the Grand Circuit races, service posts, fireworks & camps, your bounties and contracts', emit: 'atlas:open-town-life' },
  { group: 2, code: '', icon: 'people', label: 'Invite', hint: 'Invite a friend — a link (or a Discord invite) that brings them to where you stand', emit: 'atlas:open-invite' },
  { group: 2, code: '', icon: 'mic', label: 'Voice', hint: 'Voice rooms — your party, guild or city, open rooms and stages (push to talk: Caps Lock)', emit: 'atlas:open-voice-rooms' },
  { group: 2, code: '', icon: 'bank', label: 'Bank', hint: 'Bank — accounts, transfers, term deposits, loans, credit, the safe deposit box (cash at a town\'s bank terminal)', emit: 'atlas:open-bank' },
  { group: 3, code: 'F2', key: 'F2', icon: 'build', label: 'Edit', hint: 'Edit Mode — fly, sculpt, paint, place, interiors, scripts, uploads (F2; owners & admins)' },
  { group: 3, code: 'F1', key: 'F1', icon: 'box', label: 'Inventory', hint: 'Inventory — items, resources, deeds, ships, gear & schematics (F1)' },
  { group: 3, code: 'F6', icon: 'build', label: 'Craft', hint: 'Workbench — survey, crafting, rigs, market & schematic dealers (F6)' },
  { group: 3, code: '', icon: 'droid', label: 'Droids', hint: 'Droids — your datapad: status, programs, cargo, upkeep and the cost estimator (K at a droid)', emit: 'atlas:open-droids' },
  { group: 3, code: 'F10', key: 'F10', icon: 'ticket', label: 'Recipes', hint: 'Crafting Guide — everything you can craft right now and how (F10)' },
  { group: 3, code: '', icon: 'compass', label: 'Guide', hint: 'Field Guide — birds, bugs, creatures and fish you have found; fishing (E at the water)', emit: 'atlas:open-field-guide' },
  { group: 3, code: 'KeyL', icon: 'logic', label: 'Logic', hint: 'World Logic — visual scripting (L)' },
  { group: 3, code: 'KeyI', icon: 'code', label: 'Lua IDE', hint: 'Lua IDE — contracts, world & NPC scripts (I · opens in Edit Mode)' },
  { group: 4, code: 'F3', key: 'F3', icon: 'chart', label: 'Perf', hint: 'Performance HUD (F3)' },
  { group: 4, code: 'F4', key: 'F4', icon: 'people', label: 'Versonas', hint: 'Versona Lab — observe & direct the NPC town (F4)' },
  { group: 4, code: 'Backquote', key: '`', icon: 'bug', label: 'Debug', hint: 'Debug console (`)', devOnly: true },
];

/** The FLIGHT bar: every code is a SpaceModule window-keydown binding. */
const ENTRIES_SPACE: MenuEntry[] = [
  { group: 0, code: 'KeyH', key: 'h', icon: 'help', label: 'Help', hint: 'Field manual — everything you can do, searchable (H)' },
  { group: 0, code: 'Escape', key: 'Escape', icon: 'gear', label: 'Options', hint: 'Graphics & options (Esc)' },
  { group: 1, code: 'KeyG', icon: 'globe', label: 'Galaxy', hint: 'Galactic chart — every system, hyper-warp & courses (G; next to a station G repairs)' },
  { group: 1, code: 'KeyY', icon: 'shade', label: 'View', hint: 'Toggle first-person cockpit / chase view (Y)' },
  { group: 1, code: 'KeyM', icon: 'map', label: 'System', hint: 'System chart — planets, belts, stations, dens; click to autopilot (M)' },
  { group: 1, code: 'KeyN', icon: 'compass', label: 'Nav', hint: 'Cycle nav targets of this system (N)' },
  { group: 1, code: 'KeyJ', icon: 'arrow', label: 'Jump', hint: 'Hyperdrive to the nav target (J)' },
  { group: 1, code: 'Tab', key: 'Tab', icon: 'diamond', label: 'Target', hint: 'Cycle combat targets (Tab)' },
  { group: 2, code: 'KeyP', icon: 'pin', label: 'Dock', hint: 'Dock with a station / beacon / charted world (P)' },
  { group: 2, code: 'KeyX', icon: 'home', label: 'Land', hint: 'Planetfall on the world below (X)' },
  { group: 2, code: 'KeyB', icon: 'box', label: 'Station', hint: 'Board the station interior (B)' },
  { group: 2, code: 'KeyI', icon: 'exit', label: 'Deck', hint: 'Walk the Sovereign deck (I)' },
  { group: 2, code: 'KeyV', icon: 'people', label: 'Droids', hint: 'Droid command (V)' },
  { group: 3, code: 'Digit1', key: '1', icon: 'star', label: 'Weapons', hint: 'Power to weapons (1)' },
  { group: 3, code: 'Digit2', key: '2', icon: 'shade', label: 'Shields', hint: 'Power to shields (2)' },
  { group: 3, code: 'Digit3', key: '3', icon: 'chart', label: 'Engines', hint: 'Power to engines (3)' },
  // Space campaign I: these two press the action's CURRENT binding (the bar pressed R — level-out since space campaign B
  // moved the missile to Z — and never fired anything; the synthesized key also stuck in the sector's held set).
  { group: 3, code: 'KeyZ', action: 'spaceMissile', icon: 'arrow', label: 'Fire 2', hint: 'Secondary fire — the active fire group\'s mount (a homing missile by default)' },
  { group: 3, code: 'KeyC', action: 'spaceCountermeasures', icon: 'orbit', label: 'Counter', hint: 'Countermeasures — flares against inbound seekers; a sensor burst while your sensors are jammed' },
  { group: 4, code: 'Backquote', key: '`', icon: 'bug', label: 'Debug', hint: 'Debug console (`)', devOnly: true },
];

function devGated(): boolean {
  try {
    return location.hostname === 'localhost' || location.hostname === '127.0.0.1'
      || localStorage.getItem('atlas:debug') === '1';
  } catch { return false; }
}

export class AtlasMenuBarModule implements IModule {
  readonly name = 'AtlasMenuBar';

  private bar: HTMLDivElement | null = null;
  private body: HTMLDivElement | null = null;       // ground / station / deck
  private bodySpace: HTMLDivElement | null = null;  // flight
  private collapsed = false;
  private flightMode = false;

  init(_app: App): void {
    void _app;
    try { this.collapsed = localStorage.getItem('atlas:menubar-collapsed') === '1'; } catch { /* private mode */ }
    // Build after account-ready so it never covers the identity kiosk.
    bus.on('atlas:account-ready', () => this.build());
  }

  private press(entry: MenuEntry): void {
    if (entry.emit) { bus.emit(entry.emit, {}); return; }
    // A real-looking keydown from the page body — every module's window
    // listener receives it exactly like the physical key.
    const code = (entry.action ? keyBindings()[entry.action]?.[0] : undefined) ?? entry.code;
    const init = { code, key: entry.key ?? code.replace(/^Key/, '').toLowerCase(), bubbles: true, cancelable: true };
    document.body.dispatchEvent(new KeyboardEvent('keydown', init));
    // Space campaign I: a click is a press AND a release — without the keyup the synthesized key stayed in every
    // listener's held set (the bar's old R held the sector's level-out until a real R was pressed).
    document.body.dispatchEvent(new KeyboardEvent('keyup', init));
  }

  private build(): void {
    if (this.bar) return;
    const bar = document.createElement('div');
    bar.id = 'atlas-menubar';
    bar.style.cssText =
      'position:fixed;bottom:10px;left:50%;transform:translateX(-50%);z-index:35;display:flex;' +
      'align-items:center;gap:4px;background:color-mix(in srgb, var(--ax-bg) 90%, transparent);border:1px solid var(--ax-line2);' +
      'border-radius:var(--ax-win-radius);padding:5px 7px;box-shadow:0 4px 18px rgba(0,0,0,.6);' +
      'font:11px var(--ax-font);color:var(--ax-text);user-select:none';

    // Collapse chip (always visible)
    const chip = document.createElement('button');
    chip.appendChild(makeIcon('menu', 15));
    chip.title = 'Collapse / expand the menu bar';
    chip.style.cssText =
      'width:30px;height:34px;display:flex;align-items:center;justify-content:center;' +
      'background:var(--ax-bg3);color:var(--ax-text);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);cursor:pointer';
    chip.onclick = () => {
      this.collapsed = !this.collapsed;
      try { localStorage.setItem('atlas:menubar-collapsed', this.collapsed ? '1' : '0'); } catch { /* ok */ }
      this.applyCollapsed();
    };
    bar.appendChild(chip);

    this.body = this.buildBody(ENTRIES, false);
    this.bodySpace = this.buildBody(ENTRIES_SPACE, true);
    bar.append(this.body, this.bodySpace);
    document.body.appendChild(bar);
    this.bar = bar;
    this.applyCollapsed();
  }

  /** One toolbar body. The flight bar is compact (narrow buttons, 10 px
   *  labels) so it clears the ship-status panel and the radar at 1280 px. */
  private buildBody(entries: MenuEntry[], compact: boolean): HTMLDivElement {
    const body = document.createElement('div');
    body.style.cssText = `display:flex;align-items:center;gap:${compact ? 1 : 4}px`;
    let lastGroup = -1;
    for (const e of entries) {
      if (e.devOnly && !devGated()) continue;
      if (e.group !== lastGroup && lastGroup !== -1) {
        const sep = document.createElement('div');
        sep.style.cssText = `width:1px;height:26px;background:var(--ax-sel);margin:0 ${compact ? 2 : 3}px`;
        body.appendChild(sep);
      }
      lastGroup = e.group;
      const btn = document.createElement('button');
      btn.title = e.hint;
      btn.dataset.menuCode = e.code;
      btn.style.cssText =
        `display:flex;flex-direction:column;align-items:center;gap:1px;min-width:${compact ? 34 : 46}px;` +
        `padding:3px ${compact ? 2 : 6}px;background:transparent;color:var(--ax-text);border:1px solid transparent;` +
        `border-radius:var(--ax-radius);cursor:pointer;font:${compact ? 10 : 11}px system-ui`;
      const ic = document.createElement('span');
      ic.style.cssText = 'display:flex;color:var(--ax-hi)';
      ic.appendChild(makeIcon(e.icon, compact ? 15 : 16));
      btn.appendChild(ic);
      const lb = document.createElement('span');
      lb.textContent = e.label;
      lb.style.opacity = '.85';
      btn.appendChild(lb);
      btn.onmouseenter = () => { btn.style.background = 'var(--ax-bg3)'; btn.style.borderColor = 'var(--ax-line2)'; };
      btn.onmouseleave = () => { btn.style.background = 'transparent'; btn.style.borderColor = 'transparent'; };
      btn.onclick = () => this.press(e);
      body.appendChild(btn);
    }
    return body;
  }

  private applyCollapsed(): void {
    // Which bar: FLIGHT while the sector is live and the pilot is in the
    // cockpit; the ground bar on foot — including station / deck interiors.
    this.flightMode = atlas.spaceFlight && !atlas.spaceInterior;
    if (this.body) this.body.style.display = this.collapsed || this.flightMode ? 'none' : 'flex';
    if (this.bodySpace) this.bodySpace.style.display = this.collapsed || !this.flightMode ? 'none' : 'flex';
    if (!this.spaceWatch) {
      let was = this.flightMode;
      this.spaceWatch = window.setInterval(() => {
        const now = atlas.spaceFlight && !atlas.spaceInterior;
        if (now !== was) { was = now; this.applyCollapsed(); }
      }, 500);
    }
  }
  private spaceWatch = 0;

  dispose(): void {
    this.bar?.remove();
    this.bar = null;
  }
}
